import { ApplicationController } from '@aha-app/mvc';
import CommandController from './CommandController';
import AhaDrawer from 'javascripts/drawer';
import Turbolinks from 'javascripts/turbolinks';

class FredController extends ApplicationController {
  static TAB_RECENT = 'recent';
  static TAB_SEARCH_ACCOUNT = 'search-account';
  static TAB_SEARCH_MY_WORK = 'search-my-work';
  static TAB_SEARCH_CONTEXT = 'search-context';
  static TAB_COMMANDS = 'commands';
  static TAB_COMMAND_RUNNER = 'command-runner';

  static initialState = {
    // Whether the modal is currently displayed.
    open: true,
    // Current tab.
    tab: FredController.TAB_RECENT,
    // Current query.
    query: '',
    // Callback to call when modal is closed.
    onClose: () => {},
    // Command to execute when opening.
    initialCommand: null,
    // Whether we should do anything when results are clicked, or just emit events
    eventsOnly: false,
    // Whether we should display AI Chat CTA.
    showAIChatCTA: true,
  };

  // Cached query state so that re-opening the modal brings it back to the
  // same view as before.
  static cache = {
    tab: FredController.TAB_RECENT,
    query: '',
  };

  async initialize({
    initialTab,
    initialCommand,
    initialCommandArgs,
    callback,
    eventsOnly,
    onClose,
  }) {
    if (initialCommand) {
      initialTab = FredController.TAB_COMMANDS;
      this.state.initialCommand = initialCommand;
      this.state.initialCommandArgs = initialCommandArgs;
      this.state.callback = callback;
    }

    this.state.tab = initialTab || FredController.cache.tab;
    this.state.query = FredController.cache.query;
    if (onClose) this.onClose = onClose;
    if (eventsOnly) this.state.eventsOnly = eventsOnly;

    const pageInfo =
      window.AhaSearchContext && window.AhaSearchContext.pageInfo;

    // Reset In Context search if page has changed.
    if (
      !this.isInitialLoadOnContextPage(initialTab) &&
      this.hasSwitchedContextPage(pageInfo)
    ) {
      this.actionClearQuery();
    }
    FredController.cache.searchContext = pageInfo && pageInfo.searchable_type;

    this.commandController = new CommandController();
    this.commandController.internalInitialize(this, {});
  }

  actionClose() {
    this.state.open = false;
    this.onClose();
  }

  actionClearQuery() {
    this.state.query = '';
    this.state.tab = FredController.TAB_RECENT;
    this.clearCache();
  }

  actionSetQuery(value) {
    this.state.query = value;
    if (value.length > 0 && this.state.tab === FredController.TAB_RECENT) {
      this.state.tab = FredController.TAB_SEARCH_ACCOUNT;
    } else if (
      value.length === 0 &&
      this.state.tab !== FredController.TAB_RECENT &&
      this.state.tab !== FredController.TAB_SEARCH_MY_WORK &&
      this.state.tab !== FredController.TAB_COMMANDS
    ) {
      this.state.tab = FredController.TAB_RECENT;
    }

    FredController.cache.query = value;
    FredController.cache.tab = this.state.tab;
  }

  actionSelectTab(tab) {
    this.state.tab = tab;

    if (
      tab === FredController.TAB_RECENT ||
      tab === FredController.TAB_COMMANDS
    ) {
      this.state.query = '';
    }

    if (tab === FredController.TAB_SEARCH_MY_WORK) {
      window.AhaAnalytics.track(
        'Feature-7412685681697076782',
        'User clicks My work search'
      );
    }
  }

  clearCache() {
    FredController.cache = {
      tab: FredController.TAB_RECENT,
      query: '',
    };
  }

  isSearchingFromNewMyWork() {
    return window.location.pathname.includes('/user_dashboard/');
  }

  // This is a guard against clearing the query and switching to another default (Recent) tab by default
  isInitialLoadOnContextPage(initialTab) {
    return initialTab === FredController.TAB_SEARCH_CONTEXT;
  }

  hasSwitchedContextPage(pageInfo) {
    return (
      this.state.tab === FredController.TAB_SEARCH_CONTEXT &&
      (!pageInfo ||
        pageInfo.searchable_type !== FredController.cache.searchContext)
    );
  }

  actionSelectResult(result) {
    this.actionClose();

    document.dispatchEvent(
      new CustomEvent('aha.fred.result-selected', { detail: { result } })
    );

    if (!this.isSearchingFromNewMyWork() && !this.state.eventsOnly) {
      if (result.drawerable) {
        AhaDrawer.showUrl(result.url);
      } else {
        Turbolinks.visit(result.url);
      }
    }
  }

  actionDismissAIChatCTA() {
    this.state.showAIChatCTA = false;
  }
}

export default FredController;
